(function () {
  'use strict';

  var SUGGEST = '/search/suggest.json';
  var CART_GET = '/cart.js';
  var CART_ADD = '/cart/add.js';
  var CART_CHANGE = '/cart/change.js';
  var DEBOUNCE_MS = 250;
  var READ_TIMEOUT_MS = 10000;
  var OPERATION_NOTICE_MS = 10000;
  var ATTR_KEYS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_content',
    'utm_term',
    'intent',
    'oppref',
    'oa_ad_account_id',
    'oa_campaign_id',
    'oa_ad_group_id',
    'oa_ad_id'
  ];
  var ATTR_PREFIX = 'oxp_rfq_';
  var INQUIRY_KEY = ATTR_PREFIX + 'inquiry_id';
  var LANDING_KEY = ATTR_PREFIX + 'landing_page';
  var SUBMIT_CONTEXT_KEY = ATTR_PREFIX + 'submit_context';
  var ATTR_SNAPSHOT_KEY = ATTR_PREFIX + 'attribution_snapshot';
  // Retain the legacy key for pending submission evidence; it no longer
  // authorizes automatic Quote List cleanup.
  var PENDING_CLEAR_KEY = 'oxp_quote_pending_clear';
  var leadDispatchedForPage = false;
  var contactPicker = null;
  var documentInquiryId = '';
  var documentAttribution = {};
  var documentLandingPage = '';

  // ===== Helpers =====
  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function debounce(fn, ms) {
    var t;
    return function () {
      var args = arguments, ctx = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, ms);
    };
  }
  function jsonFetch(url, init) {
    init = init || {};
    init.headers = Object.assign({ 'Accept': 'application/json' }, init.headers || {});
    if (init.body && typeof init.body !== 'string') {
      init.headers['Content-Type'] = 'application/json';
      init.body = JSON.stringify(init.body);
    }
    var isRead = !init.method || init.method.toUpperCase() === 'GET';
    var controller = isRead && typeof AbortController === 'function' ? new AbortController() : null;
    if (controller) init.signal = controller.signal;
    var request = fetch(url, init).then(function (r) {
      if (!r.ok) return r.text().then(function (t) { throw new Error('HTTP ' + r.status + ': ' + t.slice(0, 200)); });
      return r.json();
    });
    // Only reads can expire safely. A write may still be saving on Shopify,
    // so its result must not be replaced with an assumed failure or retried.
    if (!isRead) return request;
    return new Promise(function (resolve, reject) {
      var timeout = setTimeout(function () {
        var error = new Error('Read request timed out');
        error.quoteMessage = url === CART_GET
          ? 'Loading your quote list took too long.'
          : 'Loading product information took too long.';
        // Reject even without AbortController; a late response must never
        // replace a newer cart readback. The timeout includes body decoding.
        reject(error);
        if (controller) controller.abort();
      }, READ_TIMEOUT_MS);
      request.then(function (value) {
        clearTimeout(timeout);
        resolve(value);
      }, function (error) {
        clearTimeout(timeout);
        reject(error);
      });
    });
  }
  function safeSessionGet(key) {
    try { return sessionStorage.getItem(key) || ''; } catch (e) { return ''; }
  }
  function safeSessionSet(key, value) {
    try { sessionStorage.setItem(key, String(value)); } catch (e) {}
  }
  function safeSessionRemove(key) {
    try { sessionStorage.removeItem(key); } catch (e) {}
  }
  function cleanAttributionValue(value, key) {
    var maxLength = key === 'oppref' ? 1024 : 200;
    return String(value || '').trim().slice(0, maxLength);
  }
  function newInquiryId() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return 'OXP-' + window.crypto.randomUUID();
    }
    return 'OXP-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
  }
  function buildInquiryId() {
    // Functional form identity must not change merely because storage fails.
    // This cache lasts only for this document; it cannot restore a navigation.
    if (!documentInquiryId) {
      documentInquiryId = safeSessionGet(INQUIRY_KEY) || newInquiryId();
      safeSessionSet(INQUIRY_KEY, documentInquiryId);
    }
    return documentInquiryId;
  }
  function pushDataLayer(eventName, details) {
    window.dataLayer = window.dataLayer || [];
    var payload = Object.assign({
      event: eventName,
      form_name: 'b2b_quote_request'
    }, getAttributionContext(), details || {});
    window.dataLayer.push(payload);
    try {
      window.dispatchEvent(new CustomEvent('openxpromo:' + eventName, {
        detail: payload
      }));
    } catch (e) {}
    try {
      if (
        window.Shopify
        && window.Shopify.analytics
        && typeof window.Shopify.analytics.publish === 'function'
      ) {
        window.Shopify.analytics.publish('openxpromo:' + eventName, payload);
      }
    } catch (e) {}
  }
  function captureAttribution() {
    var params = new URLSearchParams(window.location.search);
    // Keep landing_page's existing first-page-in-session meaning, separately
    // from the latest explicitly tagged acquisition context.
    documentLandingPage = safeSessionGet(LANDING_KEY)
      || window.location.origin + window.location.pathname;
    safeSessionSet(LANDING_KEY, documentLandingPage);
    var incomingContext = {};
    var hasTaggedTouch = false;
    ATTR_KEYS.forEach(function (key) {
      // Even an explicitly empty parameter starts a new, possibly unknown,
      // context; absent fields must never inherit an older click's identifiers.
      if (params.has(key)) hasTaggedTouch = true;
      var incoming = cleanAttributionValue(params.get(key), key);
      if (incoming) incomingContext[key] = incoming;
    });
    documentAttribution = {};
    if (hasTaggedTouch) {
      documentAttribution = incomingContext;
      // One JSON write replaces the whole touch in the same sessionStorage.
      // Remove first so a failed write normally leaves unknown on navigation,
      // rather than the previously saved source. No alternative persistence.
      safeSessionRemove(ATTR_SNAPSHOT_KEY);
      safeSessionSet(ATTR_SNAPSHOT_KEY, JSON.stringify({ version: 1, context: incomingContext }));
    } else {
      try {
        var snapshot = JSON.parse(safeSessionGet(ATTR_SNAPSHOT_KEY) || 'null');
        if (snapshot && snapshot.version === 1 && snapshot.context && typeof snapshot.context === 'object' && !Array.isArray(snapshot.context)) {
          ATTR_KEYS.forEach(function (key) {
            if (!Object.prototype.hasOwnProperty.call(snapshot.context, key) || typeof snapshot.context[key] !== 'string') return;
            var value = cleanAttributionValue(snapshot.context[key], key);
            if (value) documentAttribution[key] = value;
          });
        }
      } catch (e) {}
    }
    // Legacy per-field values have no trustworthy shared touch boundary.
    // Do not migrate them into a new source claim or merge them with a URL.
    ATTR_KEYS.forEach(function (key) {
      safeSessionRemove(ATTR_PREFIX + key);
    });
  }
  function getAttributionContext() {
    return Object.assign({}, documentAttribution);
  }
  function fillAttributionFields(form) {
    if (!form) return;
    var inquiryInput = form.querySelector('[data-quote-inquiry-id]');
    var landingInput = form.querySelector('[data-quote-landing-page]');
    if (inquiryInput) inquiryInput.value = buildInquiryId();
    if (landingInput) landingInput.value = documentLandingPage;
    ATTR_KEYS.forEach(function (key) {
      var input = form.querySelector('[data-quote-' + key.replace(/_/g, '-') + ']');
      if (input) {
        input.value = documentAttribution[key] || '';
        // Keep the attribution schema and all nonempty values. Empty marketing
        // controls need not become empty rows in the native inquiry message.
        input.disabled = input.value === '';
      }
    });
  }
  function prefillBriefIntent() {
    var select = document.querySelector('[data-brief-use-case]');
    if (!select || select.value) return;
    var incoming = cleanAttributionValue(new URLSearchParams(window.location.search).get('intent'), 'intent');
    if (!incoming) incoming = documentAttribution.intent || '';
    for (var i = 0; i < select.options.length; i++) {
      if (select.options[i].value === incoming) {
        select.value = incoming;
        return;
      }
    }
  }

  function getCart() { return jsonFetch(CART_GET); }
  function addToCart(variantId, quantity) {
    return jsonFetch(CART_ADD, { method: 'POST', body: { id: variantId, quantity: quantity || 1 } });
  }
  function changeCart(key, quantity) {
    return jsonFetch(CART_CHANGE, { method: 'POST', body: { id: key, quantity: quantity } });
  }
  function fetchProduct(handle) {
    return jsonFetch('/products/' + encodeURIComponent(handle) + '.js');
  }
  function fetchSuggest(q, limit) {
    var url = SUGGEST
      + '?q=' + encodeURIComponent(q)
      + '&resources[type]=product'
      + '&resources[options][fields]=title,product_type,variants.title,variants.sku'
      + '&resources[limit]=' + (limit || 8)
      + '&resources[options][unavailable_products]=last';
    return jsonFetch(url).then(function (data) {
      var products = (data && data.resources && data.resources.results && data.resources.results.products) || [];
      return products.map(function (p) {
        return {
          handle: String(p.handle || ''),
          title: String(p.title || ''),
          image: (p.featured_image && (p.featured_image.url || p.featured_image)) || (p.image || '')
        };
      }).filter(function (p) { return p.handle; });
    });
  }

  // ===== Header bubble (sync with cart for client-side updates) =====
  function refreshHeaderBubble(cart) {
    var iconBubble = document.querySelector('#cart-icon-bubble');
    if (!iconBubble) return;
    var bubble = iconBubble.querySelector('.cart-count-bubble');
    var n = (cart && cart.item_count) || 0;

    if (n === 0) {
      if (bubble) bubble.style.display = 'none';
      return;
    }

    if (!bubble) {
      bubble = document.createElement('div');
      bubble.className = 'cart-count-bubble';
      bubble.innerHTML = '<span aria-hidden="true">' + n + '</span><span class="visually-hidden">' + n + ' items</span>';
      iconBubble.appendChild(bubble);
    } else {
      bubble.style.display = '';
      var span = bubble.querySelector('span[aria-hidden]');
      if (span) span.textContent = n < 100 ? String(n) : '99+';
      else bubble.innerHTML = '<span aria-hidden="true">' + (n < 100 ? n : '99+') + '</span><span class="visually-hidden">' + n + ' items</span>';
    }
  }

  function refreshHeaderBubbleFromCart() {
    return getCart().then(refreshHeaderBubble).catch(function () {});
  }

  // ===== Picker (contact form) =====
  function QuotePicker(root) {
    this.root = root;
    this.chipsEl = root.querySelector('[data-quote-chips]');
    this.searchEl = root.querySelector('[data-quote-search]');
    this.dropdownEl = root.querySelector('[data-quote-dropdown]');
    this.emptyEl = root.querySelector('[data-quote-empty]');
    this.emptyHintEl = root.querySelector('[data-quote-empty-hint]');
    this.itemNumberInput = root.querySelector('[data-quote-item-number-hidden]');
    this.manualItemInput = root.querySelector('[data-quote-manual-item-number]');
    this.itemSummaryEl = root.querySelector('[data-quote-item-summary]');
    this.manualNoteEl = root.querySelector('[data-quote-manual-note]');
    this.breakdownInput = root.querySelector('[data-quote-breakdown-hidden]');
    this.errorEl = root.querySelector('[data-quote-error]');
    this.form = root.closest('form');
    this.freeRequestInput = root.querySelector('[data-quote-free-request]');
    this.textOnlyStatus = root.querySelector('[data-quote-text-only-status]');
    this.searchStatus = root.querySelector('[data-quote-search-status]');
    this.searchVersion = 0;
    this.searchDismissed = false;
    this.productRequired = root.getAttribute('data-product-required') !== 'false';
    this.quantityHelpEl = this.form ? this.form.querySelector('[data-quantity-help]') : null;

    this.results = [];
    this.activeIndex = -1;
    this.cart = { items: [], item_count: 0 };
    this.cartReady = false;
    this.cartFailed = false;
    this.pendingOperations = 0;
    this.cartQueue = Promise.resolve();
    this.recoveryMessage = '';
    this.textOnlyMode = false;
    this.operationNoticeTimer = null;
    this.operationWaitExceeded = false;

    fillAttributionFields(this.form);
    this.bindEvents();
    if (this.manualItemInput && this.itemNumberInput) {
      // Bind the safety gate before handing off the single business-field name.
      // Until this succeeds, the visible input remains a normal no-JS field.
      this.syncHidden(this.cart);
      try {
        this.manualItemInput.removeAttribute('name');
        this.itemNumberInput.setAttribute('name', 'contact[item_number]');
      } catch (error) {
        this.manualItemInput.setAttribute('name', 'contact[item_number]');
        this.itemNumberInput.removeAttribute('name');
      }
    }
    var self = this;
    this.queueCartOperation(function () {
      return self.refreshFromCart().then(function () {
        // A success document must not silently add URL items again.
        if (!document.querySelector('[data-quote-success="1"]')) return self.applyUrlParams();
      });
    });
  }

  QuotePicker.prototype.showCartMessage = function (message, recoverable) {
    if (!this.errorEl) return;
    if (this.textOnlyMode) {
      this.errorEl.textContent = 'Your Quote List will not be attached. Describe the products you need, then send the written request using the form below. Your saved list will not be cleared.';
      this.errorEl.removeAttribute('hidden');
      return;
    }
    this.errorEl.textContent = message;
    if (recoverable) {
      var retry = document.createElement('button');
      retry.type = 'button';
      retry.className = 'button button--secondary';
      retry.textContent = 'Refresh quote list';
      retry.setAttribute('data-quote-recover', '');
      this.errorEl.appendChild(document.createTextNode(' '));
      this.errorEl.appendChild(retry);
    }
    if (this.textOnlyStatus && this.freeRequestInput && (this.cartFailed || this.operationWaitExceeded)) {
      var writtenOnly = document.createElement('button');
      writtenOnly.type = 'button';
      writtenOnly.className = 'button button--secondary';
      writtenOnly.textContent = 'Continue without attaching Quote List';
      writtenOnly.setAttribute('data-quote-written-only', '');
      this.errorEl.appendChild(document.createTextNode(' You can instead send only the product request and details you type. '));
      this.errorEl.appendChild(writtenOnly);
    }
    this.errorEl.removeAttribute('hidden');
  };

  QuotePicker.prototype.chooseWrittenOnly = function () {
    if (!this.textOnlyStatus || !this.freeRequestInput || this.textOnlyMode
      || (!this.cartFailed && !this.operationWaitExceeded)) return;
    // This is an explicit attachment decision, not a successful cart read or
    // a failed write. Outstanding requests are neither cancelled nor retried.
    this.textOnlyMode = true;
    this.pendingSubmitNotice = false;
    clearTimeout(this.operationNoticeTimer);
    this.operationNoticeTimer = null;
    this.root.setAttribute('aria-busy', 'false');
    this.root.setAttribute('data-quote-text-only', 'true');
    this.searchVersion += 1;
    this.searchDismissed = true;
    this.results = [];
    this.closeDropdown();
    this.syncHidden(this.cart);
    this.chipsEl.setAttribute('hidden', '');
    if (this.emptyHintEl) this.emptyHintEl.setAttribute('hidden', '');
    this.updateQuantityHelp(0);
    this.showCartMessage('', false);
    this.freeRequestInput.focus();
  };

  QuotePicker.prototype.markCartFailed = function (error) {
    this.cartReady = false;
    this.cartFailed = true;
    this.pendingSubmitNotice = false;
    this.recoveryMessage = (error && error.quoteMessage) || 'We could not confirm your latest quote-list changes.';
    // Never leave a stale product list ready for native form submission.
    this.syncHidden({ items: [] });
    this.showCartMessage(this.recoveryMessage + ' Refresh the quote list to review what was saved. Changes will not be retried automatically.', true);
  };

  QuotePicker.prototype.queueCartOperation = function (operation, recovering) {
    var self = this;
    if (this.textOnlyMode) return Promise.resolve();
    if (this.cartFailed && !recovering) {
      this.showCartMessage(this.recoveryMessage + ' Refresh the quote list before making another change.', true);
      return Promise.resolve();
    }
    if (!this.pendingOperations) {
      this.operationWaitExceeded = false;
      this.operationNoticeTimer = setTimeout(function () {
        self.operationNoticeTimer = null;
        if (!self.pendingOperations || self.textOnlyMode) return;
        self.operationWaitExceeded = true;
        self.showCartMessage('Your Quote List is still waiting for a response. We cannot confirm whether the latest change was saved.', false);
      }, OPERATION_NOTICE_MS);
    }
    this.pendingOperations += 1;
    this.root.setAttribute('aria-busy', 'true');
    var task = this.cartQueue.then(function () {
      // An earlier uncertain write cancels later queued writes until readback.
      if (self.textOnlyMode || (self.cartFailed && !recovering)) return;
      return operation();
    }).catch(function (error) {
      self.markCartFailed(error);
    }).then(function () {
      self.pendingOperations -= 1;
      self.root.setAttribute('aria-busy', self.pendingOperations && !self.textOnlyMode ? 'true' : 'false');
      var waitedForResponse = self.operationWaitExceeded;
      if (!self.pendingOperations) {
        clearTimeout(self.operationNoticeTimer);
        self.operationNoticeTimer = null;
        self.operationWaitExceeded = false;
      }
      if (!self.pendingOperations && !self.cartFailed && !self.textOnlyMode && (self.pendingSubmitNotice || waitedForResponse)) {
        self.pendingSubmitNotice = false;
        self.showCartMessage('Quote list updated. Review the saved products, then submit again.', false);
      }
    });
    this.cartQueue = task;
    return task;
  };

  QuotePicker.prototype.recoverCart = function () {
    if (this.pendingOperations || this.textOnlyMode) return;
    var self = this;
    var message = this.recoveryMessage;
    return this.queueCartOperation(function () {
      // Read only: a failed add response may still represent a successful add.
      return self.refreshFromCart().then(function () {
        self.cartFailed = false;
        self.recoveryMessage = '';
        self.pendingSubmitNotice = false;
        self.showCartMessage(message + ' Quote list refreshed. Review the saved products before submitting; add or remove products if needed.', false);
      });
    }, true);
  };

  QuotePicker.prototype.bindEvents = function () {
    var self = this;
    var debouncedSearch = debounce(function () { self.runSearch(); }, DEBOUNCE_MS);

    if (this.manualItemInput) {
      var syncManualReferences = function () {
        // A buyer can type while the cart is loading. Keep those references,
        // but never re-serialize a stale selection during a pending/failed read.
        var syncedCart = self.cartReady && !self.cartFailed && !self.pendingOperations
          ? self.cart : { items: [] };
        self.syncHidden(syncedCart);
      };
      this.manualItemInput.addEventListener('input', syncManualReferences);
      this.manualItemInput.addEventListener('change', syncManualReferences);
    }

    if (this.errorEl) this.errorEl.addEventListener('click', function (e) {
      if (e.target.closest('[data-quote-written-only]')) {
        e.preventDefault();
        self.chooseWrittenOnly();
      } else if (e.target.closest('[data-quote-recover]')) {
        e.preventDefault();
        self.recoverCart();
      }
    });

    this.searchEl.addEventListener('input', function () {
      self.searchVersion += 1;
      self.searchDismissed = false;
      self.results = [];
      self.closeDropdown();
      if (self.searchStatus) self.searchStatus.textContent = self.searchEl.value.trim()
        ? (self.textOnlyMode ? 'Your written request will be included. Quote List will not be attached.'
          : 'Your text will be included unless you select a matching product.') : '';
      debouncedSearch();
    });
    this.searchEl.addEventListener('focus', function () {
      self.searchDismissed = false;
      if (self.results.length) self.renderDropdown();
    });
    this.searchEl.addEventListener('blur', function () {
      self.searchDismissed = true;
      self.searchVersion += 1;
      self.closeDropdown();
    });
    this.searchEl.addEventListener('keydown', function (e) { self.onKeydown(e); });

    this.dropdownEl.addEventListener('mousedown', function (e) {
      var li = e.target.closest('[data-suggest-item]');
      if (!li) return;
      e.preventDefault();
      var idx = parseInt(li.getAttribute('data-idx'), 10);
      if (!isNaN(idx)) self.selectResult(idx);
    });

    this.chipsEl.addEventListener('click', function (e) {
      var rm = e.target.closest('[data-chip-remove]');
      if (rm) self.removeChip(rm.getAttribute('data-key'));
    });
    this.chipsEl.addEventListener('input', function (e) {
      var qty = e.target.closest('[data-chip-qty]');
      if (qty) {
        var v = qty.value.replace(/[^\d]/g, '');
        if (v !== qty.value) qty.value = v;
        self.updateQty(qty.getAttribute('data-key'), v);
      }
    });

    if (this.form) {
      // Keep the optional phone input editable, including after CAPTCHA or
      // validation returns. Omit only a blank value from native serialization.
      this.form.addEventListener('formdata', function (e) {
        var phone = self.form.querySelector('[name="contact[phone]"]');
        if (phone && !phone.value.trim()) e.formData.delete('contact[phone]');
      });
      // Block unsynced submissions before Shopify's bubbling CAPTCHA handlers.
      // A valid, synced submit follows the unchanged native CAPTCHA path.
      this.form.addEventListener('submit', function (e) { self.onSubmit(e); }, true);
      if (!document.querySelector('[data-quote-success="1"]')) {
        this.form.addEventListener('focusin', function beginQuote() {
          pushDataLayer('begin_rfq');
          self.form.removeEventListener('focusin', beginQuote);
        });
      }
    }
  };

  QuotePicker.prototype.runSearch = function () {
    if (this.searchDismissed || this.textOnlyMode) return;
    var q = this.searchEl.value.trim();
    if (!q) { this.results = []; this.closeDropdown(); return; }
    var self = this;
    var version = this.searchVersion;
    if (/^https?:\/\//i.test(q)) return;
    fetchSuggest(q, 8).then(function (rows) {
      if (version !== self.searchVersion || q !== self.searchEl.value.trim()) return;
      self.results = rows;
      self.activeIndex = -1;
      self.renderDropdown();
      if (self.searchStatus) self.searchStatus.textContent = rows.length
        ? 'Choose a matching product, or keep your text to request it as written.'
        : 'No matching products. Your text is included — continue with the details below.';
    }).catch(function () {
      if (version !== self.searchVersion || q !== self.searchEl.value.trim()) return;
      self.results = [];
      self.closeDropdown();
      if (self.searchStatus) self.searchStatus.textContent = 'Search is unavailable. You can still send the name or link you entered.';
    });
  };

  QuotePicker.prototype.renderDropdown = function () {
    if (!this.results.length) {
      this.closeDropdown();
      return;
    }
    var existingHandles = {};
    (this.cart.items || []).forEach(function (it) {
      if (it.handle) existingHandles[it.handle] = true;
    });
    var self = this;
    this.dropdownEl.innerHTML = this.results.map(function (p, i) {
      var already = existingHandles[p.handle];
      var imgHtml = p.image
        ? '<img src="' + escapeHtml(p.image) + '" alt="" loading="lazy" width="40" height="40">'
        : '<span class="quote-suggest-noimg" aria-hidden="true"></span>';
      return ''
        + '<li class="quote-suggest-item' + (already ? ' quote-suggest-item--saved' : '') + (i === self.activeIndex ? ' quote-suggest-item--active' : '') + '"'
        + ' id="quote-result-' + i + '" data-suggest-item data-idx="' + i + '" role="option" aria-selected="' + (i === self.activeIndex ? 'true' : 'false') + '">'
        + imgHtml
        + '<span class="quote-suggest-meta">'
        + '<span class="quote-suggest-title">' + escapeHtml(p.title) + '</span>'
        + '</span>'
        + (already ? '<span class="quote-suggest-tag">In your list</span>' : '')
        + '</li>';
    }).join('');
    this.dropdownEl.removeAttribute('hidden');
    this.searchEl.setAttribute('aria-expanded', 'true');
    if (this.activeIndex >= 0) this.searchEl.setAttribute('aria-activedescendant', 'quote-result-' + this.activeIndex);
    else this.searchEl.removeAttribute('aria-activedescendant');
  };

  QuotePicker.prototype.closeDropdown = function () {
    this.dropdownEl.setAttribute('hidden', '');
    this.searchEl.setAttribute('aria-expanded', 'false');
    this.searchEl.removeAttribute('aria-activedescendant');
    this.activeIndex = -1;
  };

  QuotePicker.prototype.onKeydown = function (e) {
    if (e.key === 'Escape') {
      e.preventDefault();
      this.searchDismissed = true;
      this.searchVersion += 1;
      this.closeDropdown();
      return;
    }
    if (this.dropdownEl.hasAttribute('hidden')) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      this.activeIndex = Math.min(this.activeIndex + 1, this.results.length - 1);
      this.renderDropdown();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      this.activeIndex = Math.max(this.activeIndex - 1, 0);
      this.renderDropdown();
    } else if (e.key === 'Enter') {
      if (this.activeIndex >= 0 && this.results[this.activeIndex]) {
        e.preventDefault();
        this.selectResult(this.activeIndex);
      }

    }
  };

  QuotePicker.prototype.selectResult = function (idx) {
    var p = this.results[idx];
    if (!p || !p.handle) return;
    var self = this;
    var selectedQuery = this.searchEl.value;
    this.searchVersion += 1;
    this.results = [];
    this.closeDropdown();
    return this.queueCartOperation(function () {
      var added = false;
      // Resolve the variant before deduplicating; a product may have several SKUs.
      return fetchProduct(p.handle).then(function (prod) {
        if (self.textOnlyMode) return;
        var variants = prod.variants || [];
        var variant = variants.filter(function (v) { return String(v.sku || '').trim().toUpperCase() === selectedQuery.trim().toUpperCase(); })[0] || variants[0];
        if (!variant) throw new Error('no variant');
        if (self.cart.items.some(function (it) { return String(it.variant_id) === String(variant.id); })) return;
        return addToCart(variant.id, 1).then(function () { added = true; });
      }).then(function () {
        if (self.textOnlyMode) return;
        return self.refreshFromCart();
      }).then(function () {
        if (self.textOnlyMode) return;
        if (added) pushDataLayer('add_to_quote', {
          quote_item_count: self.cart.items.length,
          product_handle: p.handle,
          add_method: 'quote_picker'
        });
        if (self.searchEl.value === selectedQuery) self.searchEl.value = '';
        if (self.searchStatus) self.searchStatus.textContent = added
          ? 'Product added. Add another or continue with your project details.'
          : 'This product is already in your list. Continue with your project details.';
        self.searchEl.focus();
        if (self.errorEl) self.errorEl.setAttribute('hidden', '');
      });
    });
  };

  QuotePicker.prototype.acceptCart = function (cart) {
    if (!cart || !Array.isArray(cart.items) || cart.items.some(function (it) { return !it || typeof it.key !== 'string' || !it.key; })) {
      throw new Error('Invalid quote-list response');
    }
    this.cart = cart;
    this.renderChips(cart);
    refreshHeaderBubble(cart);
    this.cartReady = true;
  };

  QuotePicker.prototype.refreshFromCart = function () {
    var self = this;
    return getCart().then(function (cart) {
      self.acceptCart(cart);
    });
  };

  QuotePicker.prototype.renderChips = function (cart) {
    if (this.textOnlyMode) {
      this.chipsEl.setAttribute('hidden', '');
      this.syncHidden(cart);
      return;
    }
    var items = cart.items || [];
    if (!items.length) {
      this.chipsEl.setAttribute('hidden', '');
      this.chipsEl.innerHTML = '';
      if (this.emptyEl) this.emptyEl.removeAttribute('hidden');
      if (this.emptyHintEl) this.emptyHintEl.removeAttribute('hidden');
    } else {
      this.chipsEl.removeAttribute('hidden');
      if (this.emptyEl) this.emptyEl.setAttribute('hidden', '');
      if (this.emptyHintEl) this.emptyHintEl.setAttribute('hidden', '');
      this.chipsEl.innerHTML = items.map(function (it, idx) {
        var line = idx + 1;
        var sku = (it.sku || '').toUpperCase();
        var key = it.key;
        var title = it.product_title || it.title || '';
        var skuHtml = sku
          ? '<span class="quote-chip-sku">' + escapeHtml(sku) + '</span>'
          : '';
        return ''
          + '<li class="quote-chip" data-chip-key="' + escapeHtml(key) + '" data-line="' + line + '">'
          + '<span class="quote-chip-title">' + escapeHtml(title) + '</span>'
          + skuHtml
          + '<button type="button" class="quote-chip-remove" data-chip-remove data-key="' + escapeHtml(key) + '" aria-label="Remove ' + escapeHtml(sku || title) + '">×</button>'
          + '</li>';
      }).join('');
    }
    this.syncHidden(cart);
    this.updateQuantityHelp(items.length);
  };

  QuotePicker.prototype.updateQuantityHelp = function (count) {
    if (!this.quantityHelpEl) return;
    if (count >= 2) {
      this.quantityHelpEl.innerHTML = 'Applied to each of your <strong>' + count + ' selected products</strong>. Need different counts per item? Include them in <em>Logo, specifications or other requirements</em> below.';
    } else if (count === 1) {
      this.quantityHelpEl.innerHTML = 'How many of this item? For multiple price tiers, list them — e.g. <em>100, 250, 500</em>.';
    } else {
      this.quantityHelpEl.innerHTML = "How many you'd like priced. For multiple price tiers, list them — e.g. <em>100, 250, 500</em>.";
    }
  };

  QuotePicker.prototype.syncHidden = function (cart) {
    if (this.textOnlyMode) {
      // Late reads and writes must never opt this page back into attaching
      // selected products after the buyer has explicitly excluded the list.
      if (this.itemNumberInput) {
        this.itemNumberInput.value = '';
        this.itemNumberInput.disabled = true;
      }
      if (this.breakdownInput) {
        this.breakdownInput.value = '';
        this.breakdownInput.disabled = true;
      }
      this.textOnlyStatus.value = 'Buyer chose a written request only. Quote List was not attached or cleared; any pending list changes remain unconfirmed.';
      this.textOnlyStatus.disabled = false;
      return;
    }
    var items = cart.items || [];
    var selected = Object.create(null);
    var combined = [];
    items.forEach(function (it) {
      var sku = String(it.sku || '').trim().toUpperCase();
      if (sku && !selected[sku]) combined.push(sku);
      if (sku) selected[sku] = true;
    });
    var seen = Object.assign(Object.create(null), selected);
    var unconfirmed = [];
    this.manualItemNumbers().forEach(function (number) {
      var key = number.toUpperCase();
      if (seen[key]) return;
      seen[key] = true;
      combined.push(number);
      unconfirmed.push(number);
    });
    if (this.itemNumberInput) {
      this.itemNumberInput.value = combined.join(', ');
    }
    if (this.breakdownInput) {
      var selectedLines = items.map(function (it) {
        var sku = (it.sku || '').toUpperCase() || '(no SKU)';
        var title = it.product_title || it.title || '';
        return '  • ' + sku + '  — ' + title + ' | Product ID: ' + (it.product_id || '') + ' | Variant ID: ' + (it.variant_id || '') + ' | Quote List quantity: ' + it.quantity + ' | /products/' + (it.handle || '');
      }).join('\n');
      var groups = [];
      if (selectedLines) groups.push('Quote List selections:\n' + selectedLines);
      if (unconfirmed.length) groups.push('Buyer-entered item numbers — confirmation required:\n'
        + unconfirmed.map(function (number) { return '  • ' + number; }).join('\n'));
      this.breakdownInput.value = groups.join('\n\n');
    }
    if (this.itemSummaryEl) {
      this.itemSummaryEl.textContent = combined.length
        ? 'Item numbers included in this inquiry: ' + combined.join(', ')
        : '';
      if (combined.length) this.itemSummaryEl.removeAttribute('hidden');
      else this.itemSummaryEl.setAttribute('hidden', '');
    }
    if (this.manualNoteEl) {
      this.manualNoteEl.textContent = unconfirmed.length
        ? 'These buyer-entered numbers need confirmation: ' + unconfirmed.join(', ')
        : '';
      if (unconfirmed.length) this.manualNoteEl.removeAttribute('hidden');
      else this.manualNoteEl.setAttribute('hidden', '');
    }
  };

  QuotePicker.prototype.manualItemNumbers = function () {
    return String(this.manualItemInput && this.manualItemInput.value || '')
      .split(',').map(function (number) { return number.trim(); }).filter(Boolean);
  };

  QuotePicker.prototype.removeChip = function (key) {
    var self = this;
    var selected = this.cart.items.filter(function (it) { return it.key === key; })[0];
    return this.queueCartOperation(function () {
      if (!self.canChangeKey(key, selected)) return;
      return changeCart(key, 0).then(function (cart) {
        self.acceptCart(cart);
        pushDataLayer('remove_from_quote', { quote_item_count: cart.items.length });
      });
    });
  };

  QuotePicker.prototype.updateQty = function (key, qty) {
    var n = parseInt(qty, 10);
    if (isNaN(n) || n < 1) return;
    var self = this;
    var selected = this.cart.items.filter(function (it) { return it.key === key; })[0];
    return this.queueCartOperation(function () {
      if (!self.canChangeKey(key, selected)) return;
      return changeCart(key, n).then(function (cart) { self.acceptCart(cart); });
    });
  };

  QuotePicker.prototype.canChangeKey = function (key, selected) {
    if (this.cart.items.some(function (it) { return it.key === key; })) return true;
    // Discounts or properties can change keys. Do not guess between lines
    // sharing a variant, or silently discard a queued change to a surviving item.
    if (selected && this.cart.items.some(function (it) { return it.variant_id === selected.variant_id; })) {
      var error = new Error('Quote-list key changed');
      error.quoteMessage = 'Your quote list changed before this product could be updated. Review the saved products and select the change again.';
      throw error;
    }
    return false; // Already removed, for example by a duplicate removal click.
  };

  QuotePicker.prototype.findExactSku = function (sku) {
    return fetchSuggest(sku, 10).then(function (rows) {
      function next(index) {
        if (index >= rows.length) {
          var error = new Error('Requested SKU not found');
          error.quoteMessage = 'No exact match was found for requested item ' + sku + '. It was not added.';
          throw error;
        }
        return fetchProduct(rows[index].handle).then(function (prod) {
          var variants = prod.variants || [];
          for (var i = 0; i < variants.length; i++) {
            if (String(variants[i].sku || '').trim().toUpperCase() === sku) return variants[i];
          }
          return next(index + 1);
        });
      }
      return next(0);
    });
  };

  QuotePicker.prototype.applyUrlParams = function () {
    var params = new URLSearchParams(location.search);
    var rawItems = params.get('items') || '';
    if (!rawItems) return Promise.resolve();
    var skus = rawItems.split(',').map(function (s) { return s.trim().toUpperCase(); }).filter(Boolean);
    var qtys = (params.get('qty') || '').split(',').map(function (s) { return s.trim(); });
    if (!skus.length) return Promise.resolve();
    var self = this;
    return skus.reduce(function (chain, sku, i) {
      return chain.then(function () {
        if (self.textOnlyMode) return;
        // Reloading a prefilled URL must not silently add the same item again.
        if (self.cart.items.some(function (it) { return String(it.sku || '').trim().toUpperCase() === sku; })) return;
        return self.findExactSku(sku).then(function (variant) {
          if (self.textOnlyMode) return;
          var qty = parseInt(qtys[i], 10);
          if (isNaN(qty) || qty < 1) qty = 1;
          return addToCart(variant.id, qty);
        }).then(function () { if (!self.textOnlyMode) return self.refreshFromCart(); });
      });
    }, Promise.resolve());
  };

  QuotePicker.prototype.onSubmit = function (e) {
    if (!this.textOnlyMode && (this.pendingOperations || !this.cartReady || this.cartFailed)) {
      e.preventDefault();
      e.stopImmediatePropagation();
      if (this.pendingOperations) {
        this.pendingSubmitNotice = true;
        this.showCartMessage('Your quote list is still updating. Wait for it to finish, review the products, then submit again.', false);
      } else {
        this.showCartMessage(this.recoveryMessage + ' Refresh the quote list before submitting.', true);
      }
      return;
    }
    var items = this.textOnlyMode ? [] : (this.cart && this.cart.items) || [];
    var projectNotes = this.form && this.form.querySelector('[name="contact[body]"]');
    var freeRequest = this.freeRequestInput && (this.freeRequestInput.value.trim() || (projectNotes && projectNotes.value.trim()));
    var hasProductRequest = items.length || this.manualItemNumbers().length || freeRequest;
    if (this.textOnlyMode ? !freeRequest : (this.productRequired && !hasProductRequest)) {
      e.preventDefault();
      e.stopImmediatePropagation();
      this.root.classList.add('is-invalid');
      if (this.errorEl) {
        this.errorEl.textContent = this.freeRequestInput ? 'Enter a product name or reference link, select a product, or describe your project below.' : 'Enter at least one item number above, or add a product to your Quote List using the search.';
        this.errorEl.removeAttribute('hidden');
      }
      var productInput = this.manualItemInput || this.searchEl;
      productInput.focus();
      productInput.scrollIntoView({ block: 'center', behavior: 'smooth' });
      var self = this;
      var clearInvalid = function () {
        self.root.classList.remove('is-invalid');
        if (self.errorEl) self.errorEl.setAttribute('hidden', '');
        productInput.removeEventListener('input', clearInvalid);
      };
      productInput.addEventListener('input', clearInvalid);
      return;
    }
    this.syncHidden(this.cart);
    fillAttributionFields(this.form);
    var context = {
      inquiry_id: buildInquiryId(),
      quote_item_count: items.length
    };
    var submitContext = Object.assign({}, context);
    if (this.textOnlyMode) submitContext.quote_list_omitted = true;
    // Invalidate an earlier attempt before trying to save this one, so a quota
    // failure does not normally reuse stale inquiry identity or item counts.
    safeSessionRemove(SUBMIT_CONTEXT_KEY);
    safeSessionRemove(PENDING_CLEAR_KEY);
    safeSessionSet(SUBMIT_CONTEXT_KEY, JSON.stringify(submitContext));
    // Keep the attachment choice in this existing marker as well as context:
    // failure to save the larger context must not reuse the old item count.
    safeSessionSet(PENDING_CLEAR_KEY, this.textOnlyMode ? 'written-only' : '1');
    pushDataLayer('rfq_submit_attempt', context);
  };

  function initContactPicker() {
    var root = document.querySelector('[data-quote-picker]');
    if (!root) return;
    contactPicker = new QuotePicker(root);
  }

  function handleAcceptedSubmission() {
    if (leadDispatchedForPage) return;
    var pendingMode;
    try {
      pendingMode = sessionStorage.getItem(PENDING_CLEAR_KEY);
      if (!pendingMode) return;
    } catch (e) { return; }
    var success = document.querySelector('[data-quote-success="1"]');
    if (success) {
      var context = {};
      try { context = JSON.parse(safeSessionGet(SUBMIT_CONTEXT_KEY) || '{}'); } catch (e2) {}
      var listOmitted = context.quote_list_omitted === true || pendingMode === 'written-only';
      var leadPayload = {
        inquiry_id: context.inquiry_id || safeSessionGet(INQUIRY_KEY),
        quote_item_count: listOmitted ? 0 : Number(context.quote_item_count || 0),
        lead_source: 'shopify_contact_form'
      };

      // Consume the success marker before dispatching. Keep Quote List for
      // reference: stale or unavailable cross-page metadata must never grant
      // permission to erase saved products after a native inquiry succeeds.
      leadDispatchedForPage = true;
      safeSessionRemove(PENDING_CLEAR_KEY);
      safeSessionRemove(SUBMIT_CONTEXT_KEY);
      safeSessionRemove(INQUIRY_KEY);

      pushDataLayer('generate_lead', leadPayload);
      if (contactPicker) {
        // The accepted payload above keeps the submitted ID. The form still
        // visible on this success document starts a separate future inquiry,
        // even if removing the old storage value was unavailable.
        documentInquiryId = newInquiryId();
        safeSessionSet(INQUIRY_KEY, documentInquiryId);
        fillAttributionFields(contactPicker.form);
      }
    }
  }

  function initHeaderBubbleSync() {
    if (!document.querySelector('#cart-icon-bubble')) return;
    refreshHeaderBubbleFromCart();
  }

  function initProductQuoteTracking() {
    document.addEventListener('click', function (e) {
      var addButton = e.target.closest('form[data-type="add-to-cart-form"] button[name="add"]');
      if (!addButton) return;
      var parts = window.location.pathname.split('/products/');
      pushDataLayer('add_to_quote', {
        product_handle: parts.length > 1 ? parts[1].split('/')[0] : '',
        add_method: 'product_page'
      });
    }, true);
  }

  // ===== Country selector (searchable phone country code) =====
  function CountrySelector(root) {
    this.root = root;
    this.trigger = root.querySelector('[data-country-trigger]');
    this.panel = root.querySelector('[data-country-panel]');
    this.search = root.querySelector('[data-country-search]');
    this.list = root.querySelector('[data-country-list]');
    this.empty = root.querySelector('[data-country-empty]');
    this.value = root.querySelector('[data-country-value]');
    this.flagEl = root.querySelector('[data-trigger-flag]');
    this.codeEl = root.querySelector('[data-trigger-code]');
    this.numberEl = root.querySelector('.oxp-phone__number');

    if (!this.trigger || !this.panel || !this.list) return;

    this.rows = Array.prototype.slice.call(this.list.querySelectorAll('.oxp-country-row'));
    this.activeIdx = -1;
    this.visibleRows = this.rows.slice();

    this.bindEvents();
  }

  CountrySelector.prototype.bindEvents = function () {
    var self = this;

    this.trigger.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      self.toggle();
    });

    this.search.addEventListener('input', function () { self.filter(); });
    this.search.addEventListener('keydown', function (e) { self.onKeydown(e); });

    this.list.addEventListener('mousedown', function (e) {
      var row = e.target.closest('.oxp-country-row');
      if (!row || row.hidden) return;
      e.preventDefault();
      self.select(row);
    });

    this.list.addEventListener('mousemove', function (e) {
      var row = e.target.closest('.oxp-country-row');
      if (!row || row.hidden) return;
      var idx = self.visibleRows.indexOf(row);
      if (idx >= 0 && idx !== self.activeIdx) {
        self.setActive(idx);
      }
    });

    document.addEventListener('mousedown', function (e) {
      if (self.panel.hasAttribute('hidden')) return;
      if (self.root.contains(e.target)) return;
      self.close();
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !self.panel.hasAttribute('hidden')) {
        e.preventDefault();
        self.close();
        self.trigger.focus();
      }
    });
  };

  CountrySelector.prototype.toggle = function () {
    if (this.panel.hasAttribute('hidden')) this.open();
    else this.close();
  };

  CountrySelector.prototype.open = function () {
    this.panel.removeAttribute('hidden');
    this.trigger.setAttribute('aria-expanded', 'true');
    this.search.value = '';
    this.filter();
    var self = this;
    setTimeout(function () { self.search.focus(); }, 0);
  };

  CountrySelector.prototype.close = function () {
    this.panel.setAttribute('hidden', '');
    this.trigger.setAttribute('aria-expanded', 'false');
    this.activeIdx = -1;
  };

  CountrySelector.prototype.filter = function () {
    var q = this.search.value.trim().toLowerCase();
    var qDigits = q.replace(/[^\d]/g, '');
    var hasQuery = q.length > 0;

    var bucketExactCode = [];
    var bucketCodePrefix = [];
    var bucketName = [];
    var bucketHidden = [];

    for (var i = 0; i < this.rows.length; i++) {
      var row = this.rows[i];
      var name = (row.getAttribute('data-name') || '').toLowerCase();
      var code = (row.getAttribute('data-code') || '');
      var codeDigits = code.replace('+', '');

      if (!hasQuery) {
        bucketName.push(row);
        continue;
      }

      var nameMatch = name.indexOf(q) !== -1;
      var codeExact = qDigits && codeDigits === qDigits;
      var codePrefix = qDigits && codeDigits.indexOf(qDigits) === 0;
      var queryHasCode = qDigits && qDigits.indexOf(codeDigits) === 0 && codeDigits.length > 0;

      if (codeExact) {
        bucketExactCode.push(row);
      } else if (codePrefix || queryHasCode) {
        bucketCodePrefix.push(row);
      } else if (nameMatch) {
        bucketName.push(row);
      } else {
        bucketHidden.push(row);
      }
    }

    var visible = bucketExactCode.concat(bucketCodePrefix, bucketName);

    // Re-order DOM so visible rows appear first in proper sort order
    var frag = document.createDocumentFragment();
    visible.forEach(function (r) { r.hidden = false; frag.appendChild(r); });
    bucketHidden.forEach(function (r) { r.hidden = true; frag.appendChild(r); });
    this.list.appendChild(frag);

    this.visibleRows = visible;
    if (this.empty) {
      if (visible.length === 0) this.empty.removeAttribute('hidden');
      else this.empty.setAttribute('hidden', '');
    }

    this.list.scrollTop = 0;
    this.setActive(visible.length > 0 ? 0 : -1);
  };

  CountrySelector.prototype.setActive = function (idx) {
    if (this.activeIdx >= 0 && this.visibleRows[this.activeIdx]) {
      this.visibleRows[this.activeIdx].classList.remove('is-active');
    }
    this.activeIdx = idx;
    if (idx >= 0 && this.visibleRows[idx]) {
      this.visibleRows[idx].classList.add('is-active');
      var row = this.visibleRows[idx];
      var rTop = row.offsetTop;
      var rBottom = rTop + row.offsetHeight;
      var sTop = this.list.scrollTop;
      var sBottom = sTop + this.list.clientHeight;
      if (rTop < sTop) this.list.scrollTop = rTop - 4;
      else if (rBottom > sBottom) this.list.scrollTop = rBottom - this.list.clientHeight + 4;
    }
  };

  CountrySelector.prototype.onKeydown = function (e) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      this.setActive(Math.min(this.activeIdx + 1, this.visibleRows.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      this.setActive(Math.max(this.activeIdx - 1, 0));
    } else if (e.key === 'Enter') {
      if (this.activeIdx >= 0 && this.visibleRows[this.activeIdx]) {
        e.preventDefault();
        this.select(this.visibleRows[this.activeIdx]);
      }
    } else if (e.key === 'Tab') {
      this.close();
    }
  };

  CountrySelector.prototype.select = function (row) {
    var flag = row.getAttribute('data-flag') || '';
    var code = row.getAttribute('data-code') || '';
    var value = row.getAttribute('data-value') || (flag + ' ' + code);

    if (this.flagEl) this.flagEl.textContent = flag;
    if (this.codeEl) this.codeEl.textContent = code;
    if (this.value) this.value.value = value;

    this.rows.forEach(function (r) {
      r.classList.remove('is-selected');
      r.setAttribute('aria-selected', 'false');
    });
    row.classList.add('is-selected');
    row.setAttribute('aria-selected', 'true');

    this.close();
    if (this.numberEl) {
      var self = this;
      setTimeout(function () { self.numberEl.focus(); }, 0);
    }
  };

  function initCountrySelectors() {
    var roots = document.querySelectorAll('[data-country-selector]');
    for (var i = 0; i < roots.length; i++) new CountrySelector(roots[i]);
  }

  function init() {
    captureAttribution();
    prefillBriefIntent();
    initHeaderBubbleSync();
    initContactPicker();
    initProductQuoteTracking();
    initCountrySelectors();
    handleAcceptedSubmission();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
